import { Group } from '@visx/group';
import { useParentSize } from '@visx/responsive';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Bar } from '@visx/shape';

/**
 * A deliberately coarse bar chart (spec §7): the chart is for seeing a shape, the Excel export is
 * for reconciling a number. Built on visx (see SparkLine for the house pattern), which is already
 * a dependency, rather than the hand-rolled SVG this replaced. The hand-rolled version laid bars
 * out in a fixed 100-unit viewBox with a minimum bar width, so once a range grew past ~66 weekly
 * buckets the extra bars silently rendered off-canvas. scaleBand keeps its range pinned to the
 * container's actual pixel width regardless of how many bars are in the domain, so bars only ever
 * get thinner, never clipped.
 */
export const UsageBarChart = ({ buckets, ariaLabel }: { buckets: { label: string; value: number }[]; ariaLabel: string }) => {
    const { parentRef, width, height } = useParentSize({ debounceTime: 150 });

    if (buckets.length === 0) {
        return null;
    }

    const max = Math.max(...buckets.map((b) => b.value), 0);

    const xScale = scaleBand<string>({
        domain: buckets.map((b) => b.label),
        range: [0, width],
        padding: 0.1
    });
    // A zero-height domain would make scaleLinear divide by zero when mapping any value through
    // it, so an all-zero range scales against 1 instead of 0 and renders flat rather than NaN.
    const yScale = scaleLinear<number>({
        domain: [0, max || 1],
        range: [height, 0]
    });

    return (
        <figure className="w-full">
            <div ref={parentRef} className="w-full h-40">
                <svg width={width} height={height} role="img" aria-label={ariaLabel}>
                    <Group>
                        {buckets.map((bucket) => {
                            const barY = yScale(bucket.value) ?? 0;
                            const barHeight = height - barY;
                            return (
                                <Bar
                                    key={bucket.label}
                                    x={xScale(bucket.label) ?? 0}
                                    y={barY}
                                    width={xScale.bandwidth()}
                                    height={barHeight}
                                    className="fill-card-icon"
                                >
                                    <title>{`${bucket.label}: ${bucket.value.toLocaleString()}`}</title>
                                </Bar>
                            );
                        })}
                    </Group>
                </svg>
            </div>
            <figcaption className="flex justify-between text-xs opacity-70 mt-1">
                <span>{buckets[0].label}</span>
                <span>{buckets[buckets.length - 1].label}</span>
            </figcaption>
        </figure>
    );
};
